import React, { useState, useEffect } from "react";
import {
  Modal,
  PrimaryBlueButton,
  PrimaryGrayButton,
  Severity,
  Toggle
} from "@velaro/velaro-component-lib";
import { AzureIndex } from "../types";
import useToastAlert from "@/hooks/useToastAlert";
import useSubscriptions from "@/hooks/useSubscriptions";

interface Props {
  index?: AzureIndex; // If provided, we're editing; otherwise creating
  onClose: () => void;
  onSave: (index: AzureIndex) => Promise<void>;
  isLoading?: boolean;
}

/** Convert any display name into a valid Azure Search index name */
function sanitizeIndexName(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")        // spaces → hyphens
    .replace(/[^a-z0-9-]/g, "") // strip everything else
    .replace(/-+/g, "-")        // collapse multiple hyphens
    .replace(/^-|-$/g, "");     // trim leading/trailing hyphens
}

export default function IndexModal({ index, onClose, onSave, isLoading }: Props) {
  const isEditing = !!index?.id;
  const toastAlert = useToastAlert();

  const [displayName, setDisplayName] = useState(index?.name || "");
  const [formData, setFormData] = useState<AzureIndex>({
    id: index?.id || 0,
    name: index?.name || "",
    siteId: index?.siteId || 0,
    type: "Vector",
    searchSize: index?.searchSize || 3,
    semanticSearchEnabled: false
  });

  const [saving, setSaving] = useState(false);
  const { subscription } = useSubscriptions();

  useEffect(() => {
    if (index) {
      setDisplayName(index.name);
      setFormData({ ...index, type: "Vector" });
    }
  }, [index]);

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      toastAlert.displayToast(Severity.Error, "Please enter an index name");
      return;
    }

    const safeName = sanitizeIndexName(displayName);
    if (!safeName) {
      toastAlert.displayToast(Severity.Error, "Name must contain at least one letter or number");
      return;
    }

    setSaving(true);
    try {
      await onSave({ ...formData, name: safeName });
    } finally {
      setSaving(false);
    }
  };

  const previewName = sanitizeIndexName(displayName);

  return (
    <Modal maxWidth="max-w-lg" show={true}>
      <div className="bg-white p-6 rounded-2xl w-full">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Edit Index" : "Create Index"}
          </h2>
          <p className="text-sm text-slate-500">
            {isEditing
              ? "Update the index configuration"
              : "Create a knowledge index for your AI training data"}
          </p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Index Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Index Name *
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., My Knowledge Base"
              className="w-full p-2 border rounded-md text-sm"
              disabled={isEditing}
            />
            {!isEditing && displayName && previewName && previewName !== displayName.toLowerCase().replace(/\s+/g, "-") && (
              <p className="text-xs text-slate-400 mt-1">
                Will be saved as: <span className="font-mono">{previewName}</span>
              </p>
            )}
          </div>
        </div>

        {/* Semantic search — Enterprise only */}
        {subscription?.enableSemanticSearch && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-slate-700">Enhanced AI Search</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Adds AI semantic re-ranking on top of vector search for better natural language results.
                  Adds ~200–500ms per query. Best for open-ended questions; leave off for faster, equally accurate direct queries.
                </div>
              </div>
              <Toggle
                toggled={formData.semanticSearchEnabled}
                changeToggle={() =>
                  setFormData({ ...formData, semanticSearchEnabled: !formData.semanticSearchEnabled })
                }
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end mt-6 gap-3">
          <PrimaryGrayButton label="Cancel" onClick={onClose} />
          <PrimaryBlueButton
            label={saving ? "Saving..." : isEditing ? "Save" : "Create"}
            onClick={handleSubmit}
            disabled={saving || isLoading}
          />
        </div>
      </div>
    </Modal>
  );
}
